import { FC, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import PageHeaderWithBack from '../components/PageHeaderWithBack';
import PaginationBar from '../components/PaginationBar';
import { statusBadgeCx } from '../utils/statusBadges';
import type { OrderStatus } from '../api/orders';
import { Customer, fetchCustomer, AbandonedCartItem, fetchCustomerAbandonedCart } from '../api/customers';
import { CustomerVehicle, fetchCustomerVehicles } from '../api/customerVehicles';
import { FetchOrdersParams, OrderResponse, PaginatedOrdersResponse, fetchOrders } from '../api/orders';
import { Appointment, fetchCustomerAppointments } from '../api/appointments';

const PAGE_LIMIT = 10;

const formatRelative = (target: Date): string => {
  const now = new Date();
  let future = target.getTime() >= now.getTime();
  const start = future ? now : target;
  const end = future ? target : now;

  let years = end.getFullYear() - start.getFullYear();
  let months = end.getMonth() - start.getMonth();
  let days = end.getDate() - start.getDate();

  if (days < 0) {
    const prevMonth = new Date(end.getFullYear(), end.getMonth(), 0);
    days += prevMonth.getDate();
    months -= 1;
  }
  if (months < 0) {
    months += 12;
    years -= 1;
  }

  const parts: string[] = [];
  if (years > 0) parts.push(`${years} year${years === 1 ? '' : 's'}`);
  if (months > 0) parts.push(`${months} month${months === 1 ? '' : 's'}`);
  if (days > 0 || parts.length === 0) parts.push(`${days} day${days === 1 ? '' : 's'}`);

  const phrase = parts.slice(0, 2).join(' ');
  return future ? `in ${phrase}` : `${phrase} ago`;
};

const CustomerView: FC = () => {
  const navigate = useNavigate();
  const { customerId } = useParams<{ customerId: string }>();

  const [customer, setCustomer] = useState<Customer | null>(null);
  const [customerError, setCustomerError] = useState<string | null>(null);
  const [customerLoading, setCustomerLoading] = useState<boolean>(true);

  const [vehicles, setVehicles] = useState<CustomerVehicle[]>([]);
  const [vehiclesError, setVehiclesError] = useState<string | null>(null);
  const [vehiclesLoading, setVehiclesLoading] = useState<boolean>(false);

  const [orders, setOrders] = useState<OrderResponse[]>([]);
  const [ordersError, setOrdersError] = useState<string | null>(null);
  const [ordersLoading, setOrdersLoading] = useState<boolean>(false);
  const [page, setPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);

  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [appointmentsError, setAppointmentsError] = useState<string | null>(null);
  const [appointmentsLoading, setAppointmentsLoading] = useState<boolean>(false);
  const [appointmentsPage, setAppointmentsPage] = useState<number>(1);
  const [appointmentsTotalPages, setAppointmentsTotalPages] = useState<number>(1);

  const [abandoned, setAbandoned] = useState<AbandonedCartItem[]>([]);
  const [abandonedPage, setAbandonedPage] = useState<number>(1);
  const [abandonedTotalPages, setAbandonedTotalPages] = useState<number>(1);
  const [abandonedLoading, setAbandonedLoading] = useState<boolean>(false);
  const [abandonedError, setAbandonedError] = useState<string | null>(null);

  useEffect(() => {
    if (!customerId) {
      setCustomerError('Customer not found.');
      setCustomerLoading(false);
      return;
    }

    let active = true;
    setCustomerLoading(true);
    setCustomerError(null);
    fetchCustomer(customerId)
      .then((data) => {
        if (!active) return;
        setCustomer(data);
      })
      .catch((err) => {
        if (!active) return;
        setCustomerError(err instanceof Error ? err.message : 'Failed to load customer.');
      })
      .finally(() => {
        if (active) setCustomerLoading(false);
      });

    return () => {
      active = false;
    };
  }, [customerId]);

  useEffect(() => {
    if (!customerId) return;
    let active = true;
    setVehiclesLoading(true);
    setVehiclesError(null);
    fetchCustomerVehicles({ customerId })
      .then((data) => {
        if (!active) return;
        setVehicles(data);
      })
      .catch((err) => {
        if (!active) return;
        setVehiclesError(err instanceof Error ? err.message : 'Failed to load vehicles.');
      })
      .finally(() => {
        if (active) setVehiclesLoading(false);
      });
    return () => {
      active = false;
    };
  }, [customerId]);

  useEffect(() => {
    if (!customerId) return;
    let active = true;
    setOrdersLoading(true);
    setOrdersError(null);
    const params: FetchOrdersParams = { page, limit: PAGE_LIMIT, customerId };
    fetchOrders(params)
      .then((resp: PaginatedOrdersResponse) => {
        if (!active) return;
        setOrders(resp.data);
        setTotalPages(resp.meta?.totalPages ?? 1);
      })
      .catch((err) => {
        if (!active) return;
        setOrdersError(err instanceof Error ? err.message : 'Failed to load orders.');
      })
      .finally(() => {
        if (active) setOrdersLoading(false);
      });
    return () => {
      active = false;
    };
  }, [customerId, page]);

  useEffect(() => {
    if (!customerId) return;
    let active = true;
    setAppointmentsLoading(true);
    setAppointmentsError(null);
    fetchCustomerAppointments(customerId, appointmentsPage, PAGE_LIMIT)
      .then((resp) => {
        if (!active) return;
        setAppointments(resp.data);
        setAppointmentsTotalPages(resp.meta.totalPages ?? 1);
      })
      .catch((err) => {
        if (!active) return;
        setAppointmentsError(
          err instanceof Error ? err.message : 'Failed to load appointments.',
        );
      })
      .finally(() => {
        if (active) setAppointmentsLoading(false);
      });
    return () => {
      active = false;
    };
  }, [customerId, appointmentsPage]);

  useEffect(() => {
    if (!customerId) return;
    let active = true;
    setAbandonedLoading(true);
    setAbandonedError(null);
    fetchCustomerAbandonedCart(customerId, { page: abandonedPage, limit: PAGE_LIMIT })
      .then((resp) => {
        if (!active) return;
        setAbandoned(resp.data);
        setAbandonedTotalPages(resp.meta?.totalPages ?? 1);
      })
      .catch((err) => {
        if (!active) return;
        setAbandonedError(err instanceof Error ? err.message : 'Failed to load abandoned cart.');
      })
      .finally(() => {
        if (active) setAbandonedLoading(false);
      });
    return () => { active = false; };
  }, [customerId, abandonedPage]);

  const customerName = useMemo(() => {
    if (!customer) return 'Customer';
    const name = `${customer.firstName ?? ''} ${customer.lastName ?? ''}`.trim();
    return name || 'Customer';
  }, [customer]);

  return (
    <div className="mx-auto w-full max-w-[96rem] rounded-[28px] border border-slate-200/70 bg-white/95 p-8 shadow-[0_20px_45px_-32px_rgba(37,40,85,0.55)] min-h-[calc(100vh-220px)]">
      <PageHeaderWithBack
        title={`Customer – ${customerName}`}
        description="Full customer view with profile, vehicles, and orders."
        backHref="/crm"
        backLabel="Back to CRM"
      />

      {customerLoading ? (
        <p className="mt-4 text-sm text-slate-500">Loading customer…</p>
      ) : customerError ? (
        <p className="mt-4 text-sm text-danger">{customerError}</p>
      ) : (
        <div className="mt-6 grid gap-6 lg:grid-cols-[2fr_1fr]">
          <div className="space-y-6">
            <div className="rounded-2xl border border-slate-200 bg-white p-4">
              <h3 className="text-base font-semibold text-slate-700">Profile</h3>
              <dl className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2 text-sm">
                <div>
                  <dt className="text-xs uppercase tracking-wide text-slate-400">Name</dt>
                  <dd className="text-slate-700">{customerName}</dd>
                </div>
                <div>
                  <dt className="text-xs uppercase tracking-wide text-slate-400">Email</dt>
                  <dd className="text-slate-700">{customer?.email || '—'}</dd>
                </div>
                <div>
                  <dt className="text-xs uppercase tracking-wide text-slate-400">Mobile</dt>
                  <dd className="text-slate-700">{customer?.mobileNumber || '—'}</dd>
                </div>
                <div>
                  <dt className="text-xs uppercase tracking-wide text-slate-400">ERP ID</dt>
                  <dd className="text-slate-700">{customer?.erpId ?? '—'}</dd>
                </div>
              </dl>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white p-4">
              <h3 className="text-base font-semibold text-slate-700">Orders</h3>
              {ordersLoading ? (
                <p className="mt-2 text-sm text-slate-500">Loading orders…</p>
              ) : ordersError ? (
                <p className="mt-2 text-sm text-danger">{ordersError}</p>
              ) : orders.length === 0 ? (
                <p className="mt-2 text-sm text-slate-500">No orders found.</p>
              ) : (
                <div className="mt-3 overflow-x-auto">
                  <table className="min-w-full text-left text-sm text-slate-600">
                    <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                      <tr>
                        <th className="px-4 py-2 font-semibold">Order #</th>
                        <th className="px-4 py-2 font-semibold">Vehicle</th>
                        <th className="px-4 py-2 font-semibold">Status</th>
                        <th className="px-4 py-2 font-semibold">Created</th>
                        <th className="px-4 py-2 font-semibold">Updated</th>
                        <th className="px-4 py-2 font-semibold text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {orders.map((o) => {
                        const vehicleLabel = o.customerVehicle
                          ? [
                            o.customerVehicle.vehicleBrand?.name,
                            o.customerVehicle.vehicleModel?.name,
                            o.customerVehicle.vehicleRegistrationNumber,
                          ]
                            .filter(Boolean)
                            .join(' · ')
                          : '—';
                        const statusLabelMap: Record<OrderStatus, string> = {
                          initiated: 'Initiated',
                          draft: 'Draft',
                          approved: 'Approved',
                          confirmed: 'Confirmed',
                          in_progress: 'In progress',
                          completed: 'Completed',
                          verified: 'Verified',
                          done: 'Done',
                          delivered: 'Delivered',
                          cancelled: 'Cancelled',
                        };
                        const statusLabel = statusLabelMap[o.status] ?? o.status;
                        return (
                          <tr key={o.id} className="even:bg-slate-50/60">
                            <td className="px-4 py-2 font-semibold text-slate-700">{o.orderNumber}</td>
                            <td className="px-4 py-2 text-slate-600">{vehicleLabel}</td>
                            <td className="px-4 py-2">
                              <span className={statusBadgeCx(o.status)}>{statusLabel}</span>
                            </td>
                            <td className="px-4 py-2 text-slate-500">{new Date(o.createdAt).toLocaleString()}</td>
                            <td className="px-4 py-2 text-slate-500">{new Date(o.updatedAt).toLocaleString()}</td>
                            <td className="px-4 py-2 text-right">
                              <button
                                type="button"
                                className="inline-flex items-center justify-center rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600 transition hover:border-primary hover:text-primary"
                                onClick={() => navigate(`/order-management/${o.id}`, {
                                  state: { from: `/crm/view/${customerId}` }
                                })}
                              >
                                View
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
              <div className="mt-3">
                <PaginationBar
                  page={page}
                  totalPages={Math.max(1, totalPages)}
                  onPrev={() => setPage((p) => Math.max(1, p - 1))}
                  onNext={() => setPage((p) => Math.min(totalPages, p + 1))}
                  onSelect={(v) => setPage(v)}
                  disabled={ordersLoading}
                />
              </div>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white p-4">
              <h3 className="text-base font-semibold text-slate-700">
                Appointments
              </h3>
              {appointmentsLoading ? (
                <p className="mt-2 text-sm text-slate-500">
                  Loading appointments…
                </p>
              ) : appointmentsError ? (
                <p className="mt-2 text-sm text-danger">
                  {appointmentsError}
                </p>
              ) : appointments.length === 0 ? (
                <p className="mt-2 text-sm text-slate-500">
                  No appointments found.
                </p>
              ) : (
                <div className="mt-3 overflow-x-auto">
                  <table className="min-w-full text-left text-sm text-slate-600">
                    <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                      <tr>
                        <th className="px-4 py-2 font-semibold">Scheduled at</th>
                        <th className="px-4 py-2 font-semibold">Vehicle</th>
                        <th className="px-4 py-2 font-semibold">Location</th>
                        <th className="px-4 py-2 font-semibold">Status</th>
                        <th className="px-4 py-2 font-semibold text-right">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {appointments.map((a) => {
                        const vehicleLabel = a.vehicle
                          ? [
                            a.vehicle.vehicleBrand?.name,
                            a.vehicle.vehicleModel?.name,
                            a.vehicle.vehicleRegistrationNumber,
                          ]
                            .filter(Boolean)
                            .join(' · ')
                          : '—';
                        const locationLabel =
                          a.location?.name ??
                          a.locationId ??
                          '—';
                        const statusLabel = a.status.replace(/_/g, ' ');
                        return (
                          <tr key={a.id} className="even:bg-slate-50/60">
                            <td className="px-4 py-2 text-slate-600">
                              {new Date(a.scheduledAt).toLocaleString()}
                            </td>
                            <td className="px-4 py-2 text-slate-600">
                              {vehicleLabel}
                            </td>
                            <td className="px-4 py-2 text-slate-600">
                              {locationLabel}
                            </td>
                            <td className="px-4 py-2">
                              <span className="inline-flex rounded-full bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-700">
                                {statusLabel}
                              </span>
                            </td>
                            <td className="px-4 py-2 text-right">
                              <button
                                type="button"
                                className="inline-flex items-center justify-center rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600 transition hover:border-primary hover:text-primary"
                                onClick={() =>
                                  navigate(`/appointments/${a.id}`, {
                                    state: { from: `/crm/view/${customerId}` }
                                  })
                                }
                              >
                                View
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
              <div className="mt-3">
                <PaginationBar
                  page={appointmentsPage}
                  totalPages={Math.max(1, appointmentsTotalPages)}
                  onPrev={() =>
                    setAppointmentsPage((p) => Math.max(1, p - 1))
                  }
                  onNext={() =>
                    setAppointmentsPage((p) =>
                      Math.min(appointmentsTotalPages, p + 1),
                    )
                  }
                  onSelect={(v) => setAppointmentsPage(v)}
                  disabled={appointmentsLoading}
                />
              </div>
            </div>
          </div>

          <aside className="space-y-6">
            <div className="rounded-2xl border border-slate-200 bg-slate-50/60 p-4">
              <h3 className="text-base font-semibold text-slate-700">Vehicles</h3>
              {vehiclesLoading ? (
                <p className="mt-2 text-sm text-slate-500">Loading vehicles…</p>
              ) : vehiclesError ? (
                <p className="mt-2 text-sm text-danger">{vehiclesError}</p>
              ) : vehicles.length === 0 ? (
                <p className="mt-2 text-sm text-slate-500">No vehicles linked.</p>
              ) : (
                <ul className="mt-3 space-y-2 text-sm">
                  {vehicles.map((v) => (
                    <li key={v.id} className="rounded-xl border border-slate-200 bg-white px-3 py-2">
                      <div className="font-semibold text-slate-700">{v.vehicleRegistrationNumber || '—'}</div>
                      <div className="text-xs text-slate-500">
                        {[v.vehicleBrand?.name, v.vehicleModel?.name, v.vehicleRegistrationYear]
                          .filter(Boolean)
                          .join(' • ')}
                      </div>
                      <div className="text-[10px] text-slate-400 mt-1 uppercase">
                        {[
                          v.vehicleVin ? `VIN: ${v.vehicleVin}` : null,
                          v.vehicleChassisNumber ? `Chassis: ${v.vehicleChassisNumber}` : null
                        ].filter(Boolean).join(' • ')}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="rounded-2xl border border-slate-200 bg-slate-50/60 p-4">
              <h3 className="text-base font-semibold text-slate-700">Abandoned Cart</h3>
              {abandonedLoading ? (
                <p className="mt-2 text-sm text-slate-500">Loading abandoned cart…</p>
              ) : abandonedError ? (
                <p className="mt-2 text-sm text-danger">{abandonedError}</p>
              ) : abandoned.length === 0 ? (
                <p className="mt-2 text-sm text-slate-500">No abandoned items.</p>
              ) : (
                <div className="mt-3 overflow-x-auto">
                  <table className="min-w-full text-left text-sm text-slate-600">
                    <thead className="bg-slate-100 text-xs uppercase tracking-wide text-slate-500">
                      <tr>
                        <th className="px-3 py-2 font-semibold">Product</th>
                        <th className="px-3 py-2 font-semibold">Reason</th>
                        <th className="px-3 py-2 font-semibold">Reminder date</th>
                        <th className="px-3 py-2 font-semibold">Removed at</th>
                      </tr>
                    </thead>
                    <tbody>
                      {abandoned.map((a) => {
                        let reminderAt: string | null = null;
                        if (typeof a.reminderAfterDays === 'number' && a.reminderAfterDays >= 0) {
                          const base = new Date(a.removedAt);
                          const when = new Date(base);
                          when.setDate(when.getDate() + a.reminderAfterDays);
                          reminderAt = formatRelative(when);
                        }
                        return (
                          <tr key={a.id} className="even:bg-slate-50/60">
                            <td className="px-3 py-2 text-slate-700">
                              {a.productVariant?.name || a.productVariantId || '—'}
                            </td>
                            <td className="px-3 py-2 text-slate-600">{a.reasons ?? '—'}</td>
                            <td className="px-3 py-2 text-slate-600">{reminderAt ?? '—'}</td>
                            <td className="px-3 py-2 text-slate-500">{new Date(a.removedAt).toLocaleString()}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
              <div className="mt-3">
                <PaginationBar
                  page={abandonedPage}
                  totalPages={Math.max(1, abandonedTotalPages)}
                  onPrev={() => setAbandonedPage((p) => Math.max(1, p - 1))}
                  onNext={() => setAbandonedPage((p) => Math.min(abandonedTotalPages, p + 1))}
                  onSelect={(v) => setAbandonedPage(v)}
                  disabled={abandonedLoading}
                />
              </div>
            </div>
          </aside>
        </div>
      )}
    </div>
  );
};

export default CustomerView;
